/**
 * TagInput — 태그 칩 입력 + 자동완성
 *
 * - 게시판 default_tags를 추천 표시
 * - 기존 사용된 태그 자동완성 (타이핑 시 매칭)
 * - 자유 태그 입력 (Enter/쉼표로 확정)
 * - 칩 형태로 선택된 태그 표시 (X 클릭으로 삭제)
 */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Trans, t } from '@lingui/macro';
import { X, Tag } from 'lucide-react';
import { cn } from '../../lib/utils';

interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  /** 게시판에 설정된 기본 태그 */
  defaultTags?: string[];
  /** 기존 사용된 태그 (빈도순) */
  usedTags?: string[];
  placeholder?: string;
  className?: string;
}

const TagInput: React.FC<TagInputProps> = ({
  value,
  onChange,
  defaultTags = [],
  usedTags = [],
  placeholder = t`Enter tags...`,
  className,
}) => {
  const [input, setInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // 선택되지 않은 추천 태그 목록 합산 (default + used, 중복 제거)
  const allSuggestions = useMemo(() => {
    const set = new Set<string>();
    const result: string[] = [];
    // default_tags를 먼저
    defaultTags.forEach(t => { if (!set.has(t)) { set.add(t); result.push(t); } });
    // used_tags 이후
    usedTags.forEach(t => { if (!set.has(t)) { set.add(t); result.push(t); } });
    return result;
  }, [defaultTags, usedTags]);

  // 현재 입력에 매칭되는 추천 (이미 선택된 건 제외)
  const filteredSuggestions = useMemo(() => {
    const selected = new Set(value);
    const available = allSuggestions.filter(t => !selected.has(t));
    if (!input.trim()) return available.slice(0, 8); // 빈 입력이면 상위 8개
    const q = input.toLowerCase();
    return available.filter(t => t.toLowerCase().includes(q)).slice(0, 8);
  }, [allSuggestions, value, input]);

  // selectedIdx 범위 보정
  useEffect(() => {
    setSelectedIdx(0);
  }, [filteredSuggestions.length, input]);

  const addTag = useCallback((tag: string) => {
    const trimmed = tag.trim();
    if (!trimmed || value.includes(trimmed)) return;
    onChange([...value, trimmed]);
    setInput('');
    setShowSuggestions(false);
  }, [value, onChange]);

  const removeTag = useCallback((tag: string) => {
    onChange(value.filter(t => t !== tag));
  }, [value, onChange]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (showSuggestions && filteredSuggestions.length > 0 && selectedIdx < filteredSuggestions.length) {
        addTag(filteredSuggestions[selectedIdx]);
      } else if (input.trim()) {
        addTag(input);
      }
      return;
    }
    if (e.key === 'Backspace' && !input && value.length > 0) {
      removeTag(value[value.length - 1]);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx(prev => Math.min(prev + 1, filteredSuggestions.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx(prev => Math.max(prev - 1, 0));
      return;
    }
    if (e.key === 'Escape') {
      setShowSuggestions(false);
      return;
    }
  };

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* 입력 영역 */}
      <div
        className={cn(
          'flex flex-wrap items-center gap-1.5 px-2.5 py-2 rounded-lg border border-border bg-background',
          'focus-within:ring-2 focus-within:ring-ring/20 focus-within:border-primary/50 transition-all',
        )}
        onClick={() => inputRef.current?.focus()}
      >
        {/* 태그 아이콘 */}
        {value.length === 0 && !input && (
          <Tag className="size-3.5 text-muted-foreground shrink-0" />
        )}

        {/* 선택된 태그 칩 */}
        {value.map(tag => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-primary/10 text-primary text-xs font-medium"
          >
            {tag}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeTag(tag); }}
              className="hover:text-destructive transition-colors cursor-pointer"
            >
              <X className="size-3" />
            </button>
          </span>
        ))}

        {/* 텍스트 입력 */}
        <input
          ref={inputRef}
          value={input}
          onChange={e => {
            setInput(e.target.value);
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
          placeholder={value.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[60px] bg-transparent text-sm outline-none text-foreground placeholder:text-muted-foreground"
        />
      </div>

      {/* 자동완성 드롭다운 */}
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 rounded-lg border border-border bg-background shadow-lg z-50 py-1 max-h-48 overflow-y-auto animate-in fade-in slide-in-from-top-1 duration-150">
          {filteredSuggestions.map((tag, idx) => {
            const isDefault = defaultTags.includes(tag);
            return (
              <button
                key={tag}
                type="button"
                onClick={() => addTag(tag)}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-1.5 text-label transition-colors cursor-pointer',
                  idx === selectedIdx
                    ? 'bg-primary/10 text-primary'
                    : 'text-foreground/80 hover:bg-muted',
                )}
              >
                <Tag className="size-3 shrink-0 text-muted-foreground" />
                <span className="truncate">{tag}</span>
                {isDefault && (
                  <span className="text-meta-sm px-1.5 py-px rounded-full bg-primary/10 text-primary font-medium ml-auto shrink-0">
                    <Trans>Suggested</Trans>
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TagInput;
